import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import el from "date-fns/locale/el";
import { CheckCircle2, XCircle, Loader2, AlertTriangle } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { appointmentApi, AppointmentCancelDetails } from "../services/api";

// Loading this page must not cancel anything. It reads the appointment, shows the
// patient which one they are about to lose, and calls the (POST) cancel endpoint
// only from the button's onClick. Anything that merely renders the page — a
// mistaken click, a restored tab, a link scanner that executes JavaScript — now
// leaves the appointment untouched.
export const CancellationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [appointment, setAppointment] =
    useState<AppointmentCancelDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const loadAppointment = async (cancellationToken: string) => {
      try {
        const details =
          await appointmentApi.getAppointmentByCancelToken(cancellationToken);
        if (!active) return;
        setAppointment(details);
        setLoadError(null);
      } catch (err: any) {
        if (!active) return;
        console.error("Error loading appointment:", err);
        setLoadError(
          err.response?.status === 404
            ? "Δεν βρέθηκε ραντεβού με αυτόν τον σύνδεσμο ακύρωσης. Ενδέχεται να έχει ήδη ακυρωθεί."
            : "Υπήρξε ένα σφάλμα κατά την ανάκτηση του ραντεβού σας. Παρακαλώ δοκιμάστε ξανά ή επικοινωνήστε μαζί μας.",
        );
      } finally {
        if (active) setIsLoading(false);
      }
    };

    if (token) {
      loadAppointment(token);
    } else {
      setLoadError("Ο σύνδεσμος ακύρωσης δεν είναι έγκυρος.");
      setIsLoading(false);
    }

    return () => {
      active = false;
    };
  }, [token]);

  const handleCancel = async () => {
    if (!token) return;
    setIsCancelling(true);
    try {
      await appointmentApi.cancelAppointmentByToken(token);
      setIsCancelled(true);
      setCancelError(null);
    } catch (err: any) {
      console.error("Error cancelling appointment:", err);
      setCancelError(
        "Υπήρξε ένα σφάλμα κατά την ακύρωση του ραντεβού σας. Παρακαλώ επικοινωνήστε μαζί μας ή δοκιμάστε ξανά.",
      );
    } finally {
      setIsCancelling(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <Loader2 className="w-12 h-12 animate-spin mx-auto text-[#a64f3c]" />
              <p className="text-gray-600">Ανάκτηση ραντεβού...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // The appointment could not be read — nothing has been cancelled.
  if (loadError || !appointment) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="bg-red-50 text-red-900 rounded-t-lg">
            <div className="flex justify-center mb-4">
              <XCircle className="w-16 h-16 text-red-600" />
            </div>
            <CardTitle className="text-center">Σφάλμα Ακύρωσης</CardTitle>
            <CardDescription className="text-center text-red-800">
              {loadError}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 pt-4">
              <Button
                className="w-full bg-[#d0ddc6] text-[#2c3e50] hover:bg-[#c4d1ba]"
                onClick={() => navigate("/")}
              >
                Επιστροφή στην Αρχική
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isCancelled) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="bg-[#d0ddc6] text-[#2c3e50] rounded-t-lg">
            <div className="flex justify-center mb-4">
              <CheckCircle2 className="w-16 h-16 text-[#a64f3c]" />
            </div>
            <CardTitle className="text-center">Ακύρωση Επιτυχής</CardTitle>
            <CardDescription className="text-center text-[#2c3e50]">
              Το ραντεβού σας ακυρώθηκε επιτυχώς.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 pt-4">
              <p className="text-sm text-center text-[#2c3e50]">
                Εάν χρειάζεστε νέο ραντεβού, μπορείτε να κλείσετε από την αρχική
                σελίδα ή να καλέσετε τη Γραμματεία στο 2310-222799.
              </p>
              <Button
                className="w-full bg-[#d0ddc6] text-[#2c3e50] hover:bg-[#c4d1ba]"
                onClick={() => navigate("/")}
              >
                Επιστροφή στην Αρχική
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const appointmentDate = parseISO(appointment.appointment_datetime);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="bg-red-50 text-red-900 rounded-t-lg">
          <div className="flex justify-center mb-4">
            <AlertTriangle className="w-16 h-16 text-red-600" />
          </div>
          <CardTitle className="text-center">Ακύρωση Ραντεβού</CardTitle>
          <CardDescription className="text-center text-red-800">
            Θέλετε σίγουρα να ακυρώσετε το ραντεβού σας;
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 pt-4">
            <div className="bg-[#f5f8f1] border border-[#d0ddc6] rounded-lg p-4 text-center">
              <p className="text-sm text-[#2c3e50]">Ραντεβού για</p>
              <p className="font-semibold text-[#2c3e50] mt-1">
                {appointment.patient_name}
              </p>
              <p className="font-semibold text-[#2c3e50] mt-2">
                {format(appointmentDate, "EEEE, d MMMM yyyy", { locale: el })}
              </p>
              <p className="font-semibold text-[#2c3e50]">
                στις {format(appointmentDate, "HH:mm")}
              </p>
            </div>

            <p className="text-sm text-center text-[#2c3e50]">
              Η ακύρωση είναι οριστική. Για να κλείσετε νέο ραντεβού θα
              χρειαστεί να κάνετε νέα κράτηση.
            </p>

            {cancelError && (
              <p className="text-sm text-center text-red-700">{cancelError}</p>
            )}

            <Button
              className="w-full bg-red-100 text-red-900 hover:bg-red-200"
              onClick={handleCancel}
              disabled={isCancelling}
            >
              {isCancelling ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Ακύρωση ραντεβού...
                </>
              ) : (
                "Ναι, ακύρωση του ραντεβού"
              )}
            </Button>
            <Button
              className="w-full bg-[#d0ddc6] text-[#2c3e50] hover:bg-[#c4d1ba]"
              onClick={() => navigate("/")}
              disabled={isCancelling}
            >
              Όχι, διατήρηση του ραντεβού
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
